import { inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { DocumentNote, DocumentNoteRequest } from '../types/document-note';
import { DocumentNoteRepository } from './document-note.repository';

@Injectable()
export class DocumentNoteService {
  private readonly repository = inject(DocumentNoteRepository);
  private readonly injector = inject(Injector);

  readonly notes = signal<DocumentNote[]>([]);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly error = signal<unknown>(null);

  /** Whether `notes` has been loaded for the current document. Consumers that mirror the
   *  list into their own document state must wait for it, or they overwrite the document's
   *  own notes snapshot with the empty initial state. */
  readonly hasLoaded = signal(false);

  /** Load failure specifically — `error` also carries create/update/delete failures, which
   *  must not blank out an already loaded list. Not every Belegart supports notes: the
   *  service answers SALES_DOCUMENT.NOTE.READ_ALL.EXCEPTION for those. */
  readonly loadFailed = signal(false);

  async load(id: string): Promise<DocumentNote[] | null> {
    this.isLoading.set(true);
    this.error.set(null);
    this.loadFailed.set(false);
    try {
      const result = await this.repository.getAll(id);
      this.notes.set(result);
      this.hasLoaded.set(true);
      return result;
    } catch (e) {
      this.error.set(e);
      this.loadFailed.set(true);
      this.notes.set([]);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  async create(id: string, body: DocumentNoteRequest): Promise<DocumentNote | null> {
    this.isSaving.set(true);
    this.error.set(null);
    try {
      const created = await this.repository.create(id, body);
      this.notes.update((notes) => [...notes, created]);
      return created;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isSaving.set(false);
    }
  }

  async update(
    id: string,
    noteId: string,
    body: DocumentNoteRequest
  ): Promise<DocumentNote | null> {
    this.isSaving.set(true);
    this.error.set(null);
    try {
      const updated = await this.repository.update(id, noteId, body);
      this.notes.update((notes) => notes.map((n) => (n.id === noteId ? updated : n)));
      return updated;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isSaving.set(false);
    }
  }

  async delete(id: string, noteId: string): Promise<boolean> {
    this.isSaving.set(true);
    this.error.set(null);
    try {
      await this.repository.delete(id, noteId);
      this.notes.update((notes) => notes.filter((n) => n.id !== noteId));
      return true;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return false;
    } finally {
      this.isSaving.set(false);
    }
  }

  reset(): void {
    this.notes.set([]);
    this.error.set(null);
    this.loadFailed.set(false);
    this.hasLoaded.set(false);
  }
}
